import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api';
import { formatINR } from '../lib/gst';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Copy, Download, Eye, Search, Share2, Trash2, FileDown } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { bytesToDataUrl, generateInvoicePDF, downloadPDF } from '../lib/pdf';
import { toast } from 'sonner';
import InvoiceShareDialog from '../components/InvoiceShareDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useLanguage } from '../contexts/LanguageContext';

const statusOptions = ['draft', 'sent', 'paid', 'overdue', 'cancelled'];
const statusStyles = {
  draft: 'border-slate-300 bg-slate-50 text-slate-700',
  sent: 'border-amber-300 bg-amber-50 text-amber-800',
  paid: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  overdue: 'border-red-300 bg-red-50 text-red-800',
  cancelled: 'border-slate-200 bg-slate-100 text-slate-400',
};
const statusDots = {
  draft: 'bg-slate-400',
  sent: 'bg-amber-400',
  paid: 'bg-emerald-500',
  overdue: 'bg-red-500',
  cancelled: 'bg-slate-300',
};
const formatDueDate = (date) => {
  if (!date) return '-';
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const isPastDue = (date) => {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${date}T00:00:00`);
  return !Number.isNaN(due.getTime()) && due < today;
};
const effectiveStatus = (invoice) => {
  const status = invoice?.status || 'draft';
  return status === 'sent' && isPastDue(invoice?.dueDate) ? 'overdue' : status;
};

export default function History() {
  const { user, isPro, usage, refresh } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateSort, setDateSort] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState({ open: false, invoice: null, url: '', loading: false });
  const [shareInvoice, setShareInvoice] = useState(null);
  const [shareConfirmInvoice, setShareConfirmInvoice] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    if (user && user !== false) refresh();
  }, [user?.id, refresh]);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'all' || statusOptions.includes(status)) {
      setStatusFilter(status);
    }
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (user && user !== false) {
        try {
          const { data } = await apiClient.get('/invoices');
          setInvoices(data);
        } catch { setInvoices([]); }
      } else {
        setInvoices([]);
      }
      setLoading(false);
    })();
  }, [user]);

  useEffect(() => () => {
    if (preview.url) URL.revokeObjectURL(preview.url);
  }, [preview.url]);

  const filtered = invoices.filter((i) => {
    const q = query.toLowerCase();
    const matchesQuery = !q || (i.number || '').toLowerCase().includes(q) || (i.customer?.name || i.customerName || '').toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || effectiveStatus(i) === statusFilter;
    return matchesQuery && matchesStatus;
  });
  const visibleInvoices = [...filtered].sort((a, b) => {
    const aTime = new Date(`${a.date || a.created_at || ''}T00:00:00`).getTime() || new Date(a.created_at || 0).getTime();
    const bTime = new Date(`${b.date || b.created_at || ''}T00:00:00`).getTime() || new Date(b.created_at || 0).getTime();
    return dateSort === 'oldest' ? aTime - bTime : bTime - aTime;
  });
  const usedInvoices = Math.max(Number(usage?.invoices || 0), invoices.length);
  const invoiceLimitReached = user && user !== false && usage?.limit != null && usedInvoices >= Number(usage.limit);
  const visibleIds = visibleInvoices.map((inv, index) => inv.id || inv.bj_key || inv.number || String(index));
  const selectedInvoices = visibleInvoices.filter((inv, index) => selectedIds.has(inv.id || inv.bj_key || inv.number || String(index)));
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const statusLabel = (status) => t(status) || status;

  useEffect(() => {
    setSelectedIds((current) => {
      const visible = new Set(visibleIds);
      const next = new Set([...current].filter((id) => visible.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [query, statusFilter, dateSort, invoices.length]);

  const toggleSelected = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleDownload = async (inv) => {
    const bytes = await generateInvoicePDF(inv, { watermark: !isPro });
    downloadPDF(bytes, `${inv.number}.pdf`);
  };

  const downloadSelected = async () => {
    if (!selectedInvoices.length) return;
    for (const inv of selectedInvoices) {
      await handleDownload(inv);
    }
    toast.success(`${t('downloaded')} ${selectedInvoices.length} ${selectedInvoices.length === 1 ? t('invoiceSingular') : t('invoicePlural')}`);
  };

  const openPreview = async (inv) => {
    if (preview.url) URL.revokeObjectURL(preview.url);
    setPreview({ open: true, invoice: inv, url: '', loading: true });
    try {
      const bytes = await generateInvoicePDF(inv, { watermark: !isPro });
      const url = bytesToDataUrl(bytes);
      setPreview({ open: true, invoice: inv, url, loading: false });
    } catch {
      setPreview({ open: false, invoice: null, url: '', loading: false });
      toast.error(t('previewFailed'));
    }
  };

  const closePreview = () => {
    if (preview.url) URL.revokeObjectURL(preview.url);
    setPreview({ open: false, invoice: null, url: '', loading: false });
  };

  const duplicateInvoice = (inv) => {
    if (invoiceLimitReached) {
      toast.error(t('duplicateLimitReached'));
      return;
    }
    const copy = { ...inv, id: undefined, bj_key: undefined, number: '', date: new Date().toISOString().slice(0, 10), status: 'draft' };
    window.localStorage.setItem('inv_last_draft', JSON.stringify(copy));
    toast.success(t('invoiceCopied'));
    navigate('/create');
  };

  const deleteSelected = async () => {
    if (!selectedInvoices.length) return;
    const count = selectedInvoices.length;
    for (const inv of selectedInvoices) {
      if (user && user !== false && inv.id) {
        await apiClient.delete(`/invoices/${inv.id}`);
      }
    }
    const ids = new Set(selectedInvoices.map((inv) => inv.id));
    setInvoices((list) => list.filter((x) => !ids.has(x.id)));
    setSelectedIds(new Set());
    await refresh();
    toast.success(`${t('deleted')} ${count} ${count === 1 ? t('invoiceSingular') : t('invoicePlural')}`);
  };

  const updateStatus = async (inv, status) => {
    const previousStatus = inv.status || 'draft';
    setInvoices((list) => list.map((x) => (x.id === inv.id ? { ...x, status } : x)));
    try {
      const { data } = await apiClient.patch(`/invoices/${inv.id}/status`, { status });
      setInvoices((list) => list.map((x) => (x.id === inv.id ? data : x)));
      toast.success(`${t('marked')} ${statusLabel(status)}`);
    } catch {
      setInvoices((list) => list.map((x) => (x.id === inv.id ? { ...x, status: previousStatus } : x)));
      toast.error(t('statusUpdateFailed'));
    }
  };

  const beginShare = (inv) => {
    if ((inv.status || 'draft') === 'draft') {
      setShareConfirmInvoice(inv);
      return;
    }
    setShareInvoice(inv);
  };

  const confirmShare = async () => {
    const inv = shareConfirmInvoice;
    if (!inv) return;
    setShareConfirmInvoice(null);
    const previousStatus = inv.status || 'draft';
    try {
      const { data } = await apiClient.patch(`/invoices/${inv.id}/status`, { status: 'sent' });
      setInvoices((list) => list.map((x) => (x.id === inv.id ? data : x)));
      setShareInvoice(data);
      await refresh();
      toast.success(`${t('marked')} ${t('sent')}`);
    } catch {
      setInvoices((list) => list.map((x) => (x.id === inv.id ? { ...x, status: previousStatus } : x)));
      toast.error(t('couldNotMarkSent'));
    }
  };

  const exportCSV = () => {
    const rows = [
      ['Invoice #', 'Date', 'Customer', 'Status', 'Due Date', 'Taxable', 'CGST', 'SGST', 'IGST', 'Total'],
      ...visibleInvoices.map((inv) => [
        inv.number,
        inv.date,
        inv.customer?.name || inv.customerName || '',
        effectiveStatus(inv),
        inv.dueDate || '',
        inv.taxSummary?.taxableAmount || '',
        inv.taxSummary?.cgst || '',
        inv.taxSummary?.sgst || '',
        inv.taxSummary?.igst || '',
        inv.taxSummary?.grandTotal || inv.grandTotal || '',
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'billjaldi-invoices.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="history-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{t('invoiceHistory')}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {user ? t('accountInvoicesSaved') : t('loginToSaveHistory')}
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative max-w-md flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t('searchInvoices')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="history-search-input"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="all">{t('all')}</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>{statusLabel(status)}</option>
          ))}
        </select>
        <select value={dateSort} onChange={(e) => setDateSort(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="history-date-sort">
          <option value="newest">{t('newestFirst')}</option>
          <option value="oldest">{t('oldestFirst')}</option>
        </select>
        <Button variant="outline" onClick={exportCSV} disabled={!filtered.length}>
          <FileDown className="w-4 h-4 mr-2" /> CSV
        </Button>
      </div>

      {selectedInvoices.length > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm" data-testid="history-bulk-actions">
          <div className="text-sm font-semibold text-slate-700">
            {selectedInvoices.length} {t('selected')}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadSelected} data-testid="history-bulk-download">
              <Download className="w-4 h-4 mr-2" /> {t('download')}
            </Button>
            <Button variant="outline" size="sm" onClick={deleteSelected} className="text-red-600 hover:text-red-700" data-testid="history-bulk-delete">
              <Trash2 className="w-4 h-4 mr-2" /> {t('delete')}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400">{t('loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl" data-testid="history-empty-state">
          <div className="text-slate-400 mb-2">{user ? t('noInvoicesYet') : t('noHistoryWithoutLogin')}</div>
          {user ? (
            <Link to="/create" className="text-orange-600 font-bold hover:underline">{t('createFirstInvoice')} →</Link>
          ) : (
            <Link to="/login?redirect=/history" className="text-orange-600 font-bold hover:underline">{t('loginToSaveInvoices')} →</Link>
          )}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="history-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-bold w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    aria-label={t('selectAllInvoices')}
                    className="h-4 w-4 rounded border-slate-300"
                    data-testid="history-select-all"
                  />
                </th>
                <th className="px-4 py-3 font-bold">{t('invoiceHash')}</th>
                <th className="px-4 py-3 font-bold">{t('date')}</th>
                <th className="px-4 py-3 font-bold">{t('customer')}</th>
                <th className="px-4 py-3 font-bold text-right">{t('amount')}</th>
                <th className="px-4 py-3 font-bold">{t('status')}</th>
                <th className="px-4 py-3 font-bold">{t('dueDate')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {visibleInvoices.map((inv, index) => {
                const rowId = inv.id || inv.bj_key || inv.number || String(index);
                return (
                <tr key={rowId} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`history-row-${inv.number}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(rowId)}
                      onChange={() => toggleSelected(rowId)}
                      aria-label={`${t('selectInvoice')} ${inv.number}`}
                      className="h-4 w-4 rounded border-slate-300"
                      data-testid={`history-select-${inv.number}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-900 font-semibold">{inv.number}</td>
                  <td className="px-4 py-3 text-slate-600">{inv.date}</td>
                  <td className="px-4 py-3 text-slate-900">{inv.customer?.name || inv.customerName}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums font-bold">{formatINR(inv.taxSummary?.grandTotal || inv.grandTotal)}</td>
                  <td className="px-4 py-3">
                    <div className="relative inline-flex items-center">
                      <span className={`absolute left-2.5 h-2 w-2 rounded-full ${statusDots[effectiveStatus(inv)]}`} />
                      <select
                        value={effectiveStatus(inv)}
                        onChange={(e) => updateStatus(inv, e.target.value)}
                        className={`h-8 rounded-md border pl-6 pr-2 text-[10px] font-bold uppercase tracking-wider ${statusStyles[effectiveStatus(inv)]}`}
                        data-testid={`history-status-${inv.number}`}
                      >
                        {statusOptions.map((status) => (
                          <option key={status} value={status}>{statusLabel(status)}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap ${isPastDue(inv.dueDate) ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                    {formatDueDate(inv.dueDate)}
                    {(inv.status || 'draft') === 'draft' && isPastDue(inv.dueDate) && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">{t('draft')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => duplicateInvoice(inv)}
                        disabled={invoiceLimitReached}
                        title={invoiceLimitReached ? t('upgradePlan') : t('duplicate')}
                        data-testid={`history-duplicate-${inv.number}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openPreview(inv)} data-testid={`history-preview-${inv.number}`} title={t('preview')}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => beginShare(inv)} data-testid={`history-share-${inv.number}`} title={t('share')}>
                        <Share2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={preview.open} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="max-w-5xl w-[92vw] h-[88vh] p-0 overflow-hidden" data-testid="invoice-preview-dialog">
          <DialogHeader className="px-5 py-4 border-b border-slate-200">
            <DialogTitle className="font-heading text-lg">
              {t('preview')} {preview.invoice?.number || ''}
            </DialogTitle>
          </DialogHeader>
          <div className="h-[calc(88vh-73px)] bg-slate-100">
            {preview.loading ? (
              <div className="h-full flex items-center justify-center text-sm font-semibold text-slate-500">{t('generatingPreview')}</div>
            ) : preview.url ? (
              <iframe
                title={`Preview ${preview.invoice?.number || 'invoice'}`}
                src={preview.url}
                className="w-full h-full bg-white"
                data-testid="invoice-preview-frame"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!shareConfirmInvoice} onOpenChange={(open) => { if (!open) setShareConfirmInvoice(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('markSentTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('markSentBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('keepAsDraft')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmShare} className="bg-slate-900 hover:bg-slate-800 text-white">
              {t('confirmAndShare')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <InvoiceShareDialog invoice={shareInvoice} open={!!shareInvoice} onOpenChange={(open) => { if (!open) setShareInvoice(null); }} />
    </div>
  );
}
